import { HttpError } from '@alfred-systems/auth-service-api'

/**
 * auth-service reports failures two ways: a non-2xx (nestia `HttpError`, the
 * envelope in its body) and a 2xx whose envelope `status_code` is not
 * `succeeded`. Both are turned into an error shaped like an axios error
 * (`err.response.status`, `err.response.data`), which is what APIManager's
 * token refresh and sign-out logic already reads.
 */

// HTTP status to file a 2xx failure envelope under. Only 401/403 have to be
// exact: they drive the retry and sign-out. Anything else is 500.
export function statusForEnvelope(code) {
    switch (code) {
        case 'unauthorized':
        case 'token_invalid':
        case 'token_expired':
            return 401
        case 'forbidden':
            return 403
        default:
            return 500
    }
}

function toResponseError(status, data, message) {
    const error = new Error(message)
    error.name = 'AuthServiceError'
    error.response = { status, data }
    return error
}

// `HttpError.message` is the raw response body; parse it as an envelope when
// it is one (it may be an HTML gateway page instead).
function readEnvelope(body) {
    try {
        const parsed = JSON.parse(body)
        return parsed && typeof parsed === 'object' ? parsed : null
    } catch (e) {
        return null
    }
}

/**
 * Turn an `issueToken` rejection into an axios-shaped error. Anything that is
 * not an HTTP response (network failure, typia's pre-send validation) is
 * returned unchanged: a bad request body is a bug, not a refusal to retry.
 */
export function normalizeIssueTokenError(err) {
    if (!(err instanceof HttpError)) return err
    const envelope = readEnvelope(err.message)
    const code = envelope && envelope.status_code
    return toResponseError(
        err.status,
        envelope,
        `issueToken failed: ${code || `HTTP ${err.status}`}`
    )
}

/**
 * Return the token data from an `issueToken` envelope, or throw an
 * axios-shaped error for an empty body, a failure envelope, or `succeeded`
 * without `data`.
 */
export function unwrapIssueTokenResponse(res) {
    if (res && res.status_code === 'succeeded' && res.data) return res.data
    const refused = Boolean(res) && res.status_code !== 'succeeded'
    const what = !res
        ? 'empty response'
        : refused
        ? res.status_code || 'no status_code'
        : 'succeeded without data'
    throw toResponseError(
        refused ? statusForEnvelope(res.status_code) : 500,
        res || null,
        `issueToken refused: ${what}`
    )
}
